import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Search, AlertTriangle } from "lucide-react";

export const UNIDAD_TIPO_LABELS = {
  rabon: "Rabón", "3.5_ton": "3.5 Ton", tracto: "Tracto", caja: "Caja", sprinter: "Sprinter", otro: "Otro",
};

const EMPTY = { economico: "", placas: "", tipo: "otro", anio: "", dimensiones: "", caat_scac: "", notas: "" };

export default function Unidades() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [activo, setActivo] = useState("all");
  const [tipo, setTipo] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [similar, setSimilar] = useState([]);
  const [saving, setSaving] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/unidades", { params: { search, activo, tipo, skip: page * limit, limit } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, activo, tipo, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setSimilar([]); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, anio: u.anio || "" }); setSimilar([]); setOpen(true); };

  const save = async (force = false) => {
    setSaving(true);
    try {
      const payload = { ...form, anio: form.anio ? parseInt(form.anio, 10) : null };
      if (editing) {
        await api.put(`/unidades/${editing.id}`, payload);
        toast.success("Unidad actualizada");
      } else {
        await api.post(`/unidades?force=${force}`, payload);
        toast.success("Unidad creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "posible_duplicado") {
        setSimilar(detail.similar || []);
        toast.warning(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (u) => {
    try {
      await api.put(`/unidades/${u.id}`, { activo: !u.activo });
      toast.success(u.activo ? "Unidad desactivada" : "Unidad reactivada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div>
      <PageHeader
        title="Unidades"
        subtitle="Catálogo de flota propia — tracking operativo, sin montos"
        actions={
          can("unidades", "crear") && (
            <Button onClick={openCreate} data-testid="new-unidad-btn" className="gap-2">
              <Plus className="h-4 w-4" /> Nueva unidad
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por económico o placas" value={search}
            onChange={(e) => { setPage(0); setSearch(e.target.value); }} className="pl-9" data-testid="unidad-search" />
        </div>
        <Select value={tipo} onValueChange={(v) => { setPage(0); setTipo(v); }}>
          <SelectTrigger className="w-full sm:w-40" data-testid="unidad-filter-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {Object.entries(UNIDAD_TIPO_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activo} onValueChange={(v) => { setPage(0); setActivo(v); }}>
          <SelectTrigger className="w-full sm:w-36" data-testid="unidad-filter-activo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="true">Activas</SelectItem>
            <SelectItem value="false">Inactivas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Económico</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Placas</TableHead>
              <TableHead>Año</TableHead>
              <TableHead>Dimensiones</TableHead>
              <TableHead>SCAC/CAAT</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((u) => (
              <TableRow key={u.id} data-testid={`unidad-row-${u.economico}`} className={!u.activo ? "opacity-60" : ""}>
                <TableCell className="font-mono text-xs font-medium">{u.economico}</TableCell>
                <TableCell className="text-sm">{UNIDAD_TIPO_LABELS[u.tipo] || u.tipo}</TableCell>
                <TableCell className="font-mono text-xs">{u.placas || "—"}</TableCell>
                <TableCell className="text-xs">{u.anio || "—"}</TableCell>
                <TableCell className="text-xs">{u.dimensiones || "—"}</TableCell>
                <TableCell className="text-xs">{u.caat_scac || "—"}</TableCell>
                <TableCell>
                  {u.activo
                    ? <Badge className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-50">Activa</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactiva</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {can("unidades", "editar") && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-unidad-${u.economico}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {can("unidades", "editar") && (
                      <Button variant="ghost" size="sm" onClick={() => toggleActivo(u)} data-testid={`toggle-unidad-${u.economico}`}>
                        {u.activo ? "Desactivar" : "Reactivar"}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Sin unidades registradas.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span data-testid="unidades-total-count">{data.total} unidad(es)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>Pág. {page + 1} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-heading">{editing ? "Editar unidad" : "Nueva unidad"}</SheetTitle></SheetHeader>

          {similar.length > 0 && (
            <div className="my-4 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="unidad-duplicate-warning">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Posible duplicado
              </div>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                {similar.map((s) => (<li key={s.id} className="font-mono text-xs">• {s.economico}</li>))}
              </ul>
              <Button size="sm" className="mt-3" variant="outline" onClick={() => save(true)} data-testid="unidad-force-create">
                Crear de todos modos
              </Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Económico *">
              <Input value={form.economico} onChange={(e) => setForm({ ...form, economico: e.target.value })} data-testid="unidad-form-economico" />
            </Field>
            <Field label="Tipo">
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="unidad-form-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(UNIDAD_TIPO_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Placas"><Input value={form.placas} onChange={(e) => setForm({ ...form, placas: e.target.value })} data-testid="unidad-form-placas" /></Field>
            <Field label="Año"><Input type="number" value={form.anio} onChange={(e) => setForm({ ...form, anio: e.target.value })} data-testid="unidad-form-anio" /></Field>
            <Field label="Dimensiones" className="sm:col-span-2">
              <Input value={form.dimensiones} onChange={(e) => setForm({ ...form, dimensiones: e.target.value })} data-testid="unidad-form-dimensiones" />
            </Field>
            <Field label="CAAT / SCAC" className="sm:col-span-2">
              <Input value={form.caat_scac} onChange={(e) => setForm({ ...form, caat_scac: e.target.value })} data-testid="unidad-form-caat-scac" />
            </Field>
            <Field label="Notas" className="sm:col-span-2">
              <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="unidad-form-notas" />
            </Field>
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => save(false)} disabled={saving || !form.economico} data-testid="unidad-save-btn">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
